import React, { useState, useEffect, useRef } from 'react';
import { Input, Icon, Alert, Form, Button } from 'antd';
import { extractDomainAndHomepage } from '../../../deepAnalysis/utils';
import { useFetchWebsiteIndexEntriesQuery, useLazyValidateDomainQuery } from '../../../deepAnalysis/websiteIndexEntry/service';
//import FirstStepButtons from '../FirstStepButtons';
import useGetPlanSettings from '../../../sales/hooks/useGetPlanSettings';


type Props = {
    domain: string;
    fromOnboarding: boolean;
    onDomainChange: (domain: string) => void;
    onSkipOnboarding: () => void;
    onFinishThisStep: () => void;
};


export default function InputDomainStep({ domain, fromOnboarding, onDomainChange, onSkipOnboarding, onFinishThisStep }: Props) {
    const [inputDomainError, setInputDomainError] = useState<string>('');
    const [showMaxDomainsError, setShowMaxDomainsError] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    const { data: websiteIndexEntries, isLoading: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const existingWebsiteDomains = websiteIndexEntries ? websiteIndexEntries.map((entry) => entry.domain) : [];

    const [triggerValidateDomain, { isLoading: isValidatingDomain }] = useLazyValidateDomainQuery();
    const { dpMaxDomains } = useGetPlanSettings();

    const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const domain = event.target.value.trim().toLowerCase();
        setInputDomainError("");
        setShowMaxDomainsError(false);
        onDomainChange(domain);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.stopPropagation();
        }
    };

    async function validateDomain() {
        const { error: invalidDomainError } = extractDomainAndHomepage(domain);

        if (invalidDomainError) {
            setInputDomainError(invalidDomainError);
            return;
        }

        if (existingWebsiteDomains.includes(domain)) {
            setInputDomainError("This domain is already added!");
            return;
        }

        if (!websiteIndexEntries || !dpMaxDomains) {
            return;
        }

        if (websiteIndexEntries.length >= dpMaxDomains) {
            setShowMaxDomainsError(true);
            return;
        }

        try {
            await triggerValidateDomain({ domain }, true).unwrap();
        } catch (error) {
            setInputDomainError("Invalid domain");
            return;
        }

        onFinishThisStep();
    };


    useEffect(() => {
        const listener = async (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === "NumpadEnter") {
                event.preventDefault();
                const buttonElement = buttonRef.current;
                if (buttonElement) {
                    const nextButton = buttonElement.querySelector('.next-btn');
                    if (nextButton) {
                        (nextButton as HTMLButtonElement).click();
                    }
                }
            }
        };

        const formElement = containerRef.current;
        formElement?.addEventListener('keydown', listener);

        return () => {
            formElement?.removeEventListener('keydown', listener);
        };
    }, []);

    return (
        <div className="steps-content">
            <Form className="step-form">
                <div ref={containerRef}>
                    <Form.Item>
                        <p>Track new website in order to have access to dashboard reports at any time</p>
                    </Form.Item>

                    <Form.Item
                        validateStatus={inputDomainError ? "error" : "validating"}
                        help={inputDomainError ? inputDomainError : ""}
                    >
                        <Input
                            id="main_url"
                            className="website-input"
                            autoCorrect="off"
                            autoCapitalize="off"
                            prefix={<Icon type="plus" style={{ color: "rgba(0,0,0,.25)", fontSize: "20px" }} />}
                            disabled={isLoadingWebsiteIndexEntries}
                            placeholder="example.com"
                            name="url"
                            size="large"
                            value={domain}
                            onChange={handleDomainChange}
                            onKeyDown={handleKeyDown}
                        />
                    </Form.Item>

                    {showMaxDomainsError && (
                        <Form.Item>
                            <Alert
                                className="deep-analysis-website-form-alert"
                                type="error"
                                showIcon
                                message="Looks like you've hit your domain limit!"
                            />
                        </Form.Item>
                    )}
                </div>
            </Form>

            <div className="steps-action">
                {/* <FirstStepButtons
                    disabled={isLoadingWebsiteIndexEntries || showMaxDomainsError}
                    showSkip={fromOnboarding}
                    isLoading={isValidatingDomain}
                    onSkipClicked={onSkipOnboarding}
                    onNextClicked={validateDomain}
                /> */}

                {fromOnboarding && (
                    <div className="button-wrapper">
                        <span
                            className="fake-link"
                            onClick={onSkipOnboarding}
                        >
                            Skip for now
                        </span>
                    </div>
                )}

                <div className="button-wrapper next-button" ref={buttonRef}>
                    <Button
                        className="next-btn right"
                        type="primary"
                        loading={isValidatingDomain}
                        onClick={validateDomain}
                        disabled={isLoadingWebsiteIndexEntries || showMaxDomainsError}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
}